import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Megaphone, ClipboardList, MessageSquare, TrendingUp } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const thisMonth = () => new Date().toISOString().slice(0, 7);

const Metric = ({ label, value, testid }) => (
  <div className="rounded-lg border border-brand-line bg-white p-4" data-testid={testid}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className="mt-1 font-display text-2xl font-semibold text-brand-ink">{value}</p>
  </div>
);

const MetricGrid = ({ m }) => (
  <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
    <Metric label="Total jobs (MTD)" value={m.total_jobs} testid="bc-m-jobs" />
    <Metric label="Bookings" value={m.bookings} testid="bc-m-bookings" />
    <Metric label="Conversions" value={`${m.conversions} (${m.conversion_rate}%)`} testid="bc-m-conversions" />
    <Metric label="Revenue (month)" value={money(m.revenue)} testid="bc-m-revenue" />
    <Metric label="Profit (month)" value={money(m.profit)} testid="bc-m-profit" />
  </div>
);

// ---------- Brand Consultant's own dashboard ----------
export const BrandConsultantDashboard = () => {
  const month = thisMonth();
  const [ov, setOv] = useState(null);
  const [studioId, setStudioId] = useState("all");
  const [brief, setBrief] = useState({ items: [] });
  const [briefText, setBriefText] = useState("");
  const [response, setResponse] = useState("");
  const [msg, setMsg] = useState({ text: "", published: false });

  const loadOv = useCallback((sid) => {
    api.get("/bc/overview", { params: sid && sid !== "all" ? { studio_id: sid } : {} })
      .then(({ data }) => setOv(data)).catch(() => {});
  }, []);
  const loadRest = useCallback(() => {
    api.get("/bc/brief", { params: { month } }).then(({ data }) => {
      setBrief(data); setBriefText((data.items || []).map((i) => i.text).join("\n"));
    }).catch(() => {});
    api.get("/bc/response", { params: { month } }).then(({ data }) => setResponse(data.text || "")).catch(() => {});
    api.get("/bc/message", { params: { month } }).then(({ data }) => setMsg(data)).catch(() => {});
  }, [month]);
  useEffect(() => { loadOv("all"); loadRest(); }, [loadOv, loadRest]);

  const submitBrief = async () => {
    try {
      const items = briefText.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put("/bc/brief", { month, items });
      toast.success("Brief submitted to HQ"); loadRest();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const publishMsg = async () => {
    try {
      await api.put("/bc/message", { month, text: msg.text, published: true });
      toast.success("Monthly message published to all studios"); loadRest();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const done = (brief.items || []).filter((i) => i.done).length;
  const shown = studioId === "all" ? ov?.network : ov?.selected;

  return (
    <div className="space-y-6" data-testid="brand-consultant-dashboard">
      <div className="flex items-center gap-2">
        <Megaphone className="h-5 w-5 text-brand-pink" />
        <h1 className="font-display text-2xl font-semibold text-brand-ink">Brand Consultant</h1>
      </div>

      <Card className="border-brand-line shadow-none">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2 text-lg">
            <TrendingUp className="h-4 w-4 text-brand-pink" /> Activity summary · {month}
          </CardTitle>
          <Select value={studioId} onValueChange={(v) => { setStudioId(v); loadOv(v); }}>
            <SelectTrigger className="w-56" data-testid="bc-studio-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All studios (network)</SelectItem>
              {(ov?.studios || []).map((s) => (
                <SelectItem key={s.studio_id} value={s.studio_id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>{shown ? <MetricGrid m={shown} /> : <p className="text-sm text-slate-500">Loading…</p>}</CardContent>
      </Card>

      <Card className="border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ClipboardList className="h-4 w-4 text-brand-pink" /> Monthly activity brief
            {(brief.items || []).length > 0 && (
              <span className="text-sm font-normal text-slate-500" data-testid="bc-done-summary">
                · {done}/{brief.items.length} acted on
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-slate-500">One suggested activity per line. HQ can tick each as done.</p>
          <Textarea rows={6} value={briefText} data-testid="bc-brief-textarea"
            onChange={(e) => setBriefText(e.target.value)} placeholder="e.g. Run a festive PPF campaign" />
          <Button size="sm" onClick={submitBrief} data-testid="bc-brief-submit">Submit brief to HQ</Button>
          {(brief.items || []).length > 0 && (
            <ul className="mt-2 space-y-1.5" data-testid="bc-brief-status">
              {brief.items.map((it) => (
                <li key={it.id} className="flex items-center gap-2 text-sm">
                  <span className={`h-2.5 w-2.5 rounded-full ${it.done ? "bg-emerald-500" : "bg-slate-300"}`} />
                  <span className={it.done ? "text-slate-400 line-through" : "text-brand-ink"}>{it.text}</span>
                  {it.done && it.done_by && <span className="text-xs text-emerald-600">✓ {it.done_by}</span>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <MessageSquare className="h-4 w-4 text-brand-pink" /> Marketing Manager's response
          </CardTitle>
        </CardHeader>
        <CardContent>
          {response ? (
            <p className="whitespace-pre-wrap text-sm text-brand-ink" data-testid="bc-response-view">{response}</p>
          ) : (
            <p className="text-sm text-slate-500">No response from the Marketing Manager yet.</p>
          )}
        </CardContent>
      </Card>

      <Card className="border-brand-line shadow-none">
        <CardHeader>
          <CardTitle className="text-lg">Monthly message to all studios</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea rows={4} value={msg.text} data-testid="bc-message-textarea"
            onChange={(e) => setMsg((m) => ({ ...m, text: e.target.value }))}
            placeholder="A single message shown to all studio managers & owners" />
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={publishMsg} data-testid="bc-message-publish">Publish to all studios</Button>
            {msg.published && <span className="text-xs text-emerald-600">Published</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ---------- CEO / Marketing Manager panel: tick activities + respond ----------
export const BrandBriefPanel = () => {
  const { user } = useAuth();
  const month = thisMonth();
  const isMM = user.role === "central_marketing";
  const [brief, setBrief] = useState({ items: [] });
  const [response, setResponse] = useState("");

  const load = useCallback(() => {
    api.get("/bc/brief", { params: { month } }).then(({ data }) => setBrief(data)).catch(() => {});
    api.get("/bc/response", { params: { month } }).then(({ data }) => setResponse(data.text || "")).catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const tick = async (it, done) => {
    try { await api.patch(`/bc/brief/${month}/items/${it.id}`, { done }); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveResponse = async () => {
    try { await api.put("/bc/response", { month, text: response }); toast.success("Response saved"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!(brief.items || []).length && !response) {
    return (
      <Card className="mb-6 border-brand-line shadow-none" data-testid="brand-brief-panel">
        <CardHeader><CardTitle className="text-lg">Brand Consultant brief</CardTitle></CardHeader>
        <CardContent><p className="text-sm text-slate-500">No brief submitted for {month} yet.</p></CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6 border-brand-line shadow-none" data-testid="brand-brief-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Megaphone className="h-4 w-4 text-brand-pink" /> Brand Consultant brief · {month}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="space-y-2">
          {(brief.items || []).map((it) => (
            <li key={it.id} className="flex items-start gap-2" data-testid={`bc-panel-item-${it.id}`}>
              <Checkbox checked={it.done} className="mt-0.5" data-testid={`bc-panel-check-${it.id}`}
                onCheckedChange={(v) => tick(it, !!v)} />
              <div>
                <p className={`text-sm ${it.done ? "text-slate-400 line-through" : "text-brand-ink"}`}>{it.text}</p>
                {it.done && it.done_by && <p className="text-[11px] text-emerald-600">Done by {it.done_by}</p>}
              </div>
            </li>
          ))}
        </ul>
        <div className="border-t border-brand-line pt-3">
          <p className="mb-1.5 text-sm font-semibold text-brand-ink">Response brief {isMM ? "" : "(Marketing Manager)"}</p>
          {isMM ? (
            <>
              <Textarea rows={4} value={response} data-testid="bc-panel-response-textarea"
                onChange={(e) => setResponse(e.target.value)}
                placeholder="What you plan to do based on these suggestions" />
              <Button size="sm" className="mt-2" onClick={saveResponse} data-testid="bc-panel-response-save">
                Save response
              </Button>
            </>
          ) : response ? (
            <p className="whitespace-pre-wrap text-sm text-brand-ink">{response}</p>
          ) : (
            <p className="text-sm text-slate-500">Awaiting the Marketing Manager's response.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// ---------- Studio dashboard: read-only monthly message ----------
export const BcMonthlyMessage = () => {
  const [msg, setMsg] = useState(null);
  useEffect(() => {
    api.get("/bc/message/current").then(({ data }) => setMsg(data)).catch(() => {});
  }, []);
  if (!msg || !msg.published || !msg.text) return null;
  return (
    <Card className="mb-6 border-crimson-200 bg-crimson-50/40 shadow-none" data-testid="bc-monthly-message">
      <CardHeader className="flex-row items-center gap-2 space-y-0 pb-2">
        <Megaphone className="h-4 w-4 text-crimson-600" />
        <CardTitle className="text-base">Message from the Brand Consultant</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="whitespace-pre-wrap text-sm text-brand-ink">{msg.text}</p>
      </CardContent>
    </Card>
  );
};
